import React, { useEffect, useState } from 'react';
import { Alert, Avatar, Box, Button, CircularProgress, Container, Paper, Switch, TextField, Typography } from '@mui/material';
import { BellRing, Copy, KeyRound, Mail, Save, Sparkles, UserRound } from 'lucide-react';
import {
  getSignupInviteStatus,
  getNotificationPreferences,
  getUserInterests,
  getUserProfile,
  resendEmailVerification,
  updateEmail,
  updatePassword,
  updateUserInterests,
  updateUsername,
} from '../utils/api';
import {
  currentPushDeviceRegistered,
  disablePushPreference,
  enablePushPreference,
  firebasePushConfigured,
  firebasePushSupported,
  pushPermissionState,
} from '../utils/firebaseNotifications';
import SubscriptionPanel from '../components/SubscriptionPanel';

const NOTIFICATION_OPTIONS = [
  {
    type: 'due_date_reminder',
    label: 'Due-date reminders',
    description: 'Remind me when assigned work becomes due.',
  },
  {
    type: 'friend_request',
    label: 'Friend requests',
    description: 'Tell me when someone sends me a friend request.',
  },
  {
    type: 'shared_task',
    label: 'Shared tasks',
    description: 'Tell me when a friend shares a task with me.',
  },
  {
    type: 'task_timer',
    label: 'Task timers',
    description: 'Alert me at zero and every additional hour past the estimate.',
  },
  {
    type: 'event_updates',
    label: 'Event invitations and updates',
    description: 'Tell me about event invitations, changes, and cancellations.',
  },
  {
    type: 'event_preparation',
    label: 'Event preparation reminders',
    description: 'Remind me when preparation should start, or 15 minutes before an event with no connected tasks.',
  },
];

const notificationPreferenceState = (response) => Object.fromEntries(
  NOTIFICATION_OPTIONS.map(({ type }) => [
    type,
    Boolean(response.preferences?.[type]?.enabled),
  ]),
);

const Profile = () => {
  const [username, setUsername] = useState('');
  const [initialUsername, setInitialUsername] = useState('');
  const [email, setEmail] = useState('');
  const [initialEmail, setInitialEmail] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [signupInvite, setSignupInvite] = useState({ enabled: false, invite_code: null, invite_url: null });
  const [profileImageUrl, setProfileImageUrl] = useState('');
  const [interestsText, setInterestsText] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [usernameStatus, setUsernameStatus] = useState({ type: '', text: '' });
  const [interestsStatus, setInterestsStatus] = useState({ type: '', text: '' });
  const [passwordStatus, setPasswordStatus] = useState({ type: '', text: '' });
  const [emailStatus, setEmailStatus] = useState({ type: '', text: '' });
  const [inviteStatus, setInviteStatus] = useState({ type: '', text: '' });
  const [savingUsername, setSavingUsername] = useState(false);
  const [savingInterests, setSavingInterests] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState({
    preferences: notificationPreferenceState({ preferences: {} }),
    activeRegistrations: 0,
  });
  const [notificationCapability, setNotificationCapability] = useState({
    configured: firebasePushConfigured(),
    permission: pushPermissionState(),
    supported: false,
  });
  const [notificationStatus, setNotificationStatus] = useState({ type: '', text: '' });
  const [savingNotifications, setSavingNotifications] = useState(false);

  useEffect(() => {
    const loadProfile = async () => {
      setLoading(true);
      try {
        const [profile, interestsData, notificationsData, pushSupported, signupInviteData] = await Promise.all([
          getUserProfile(),
          getUserInterests(),
          getNotificationPreferences(),
          firebasePushSupported(),
          getSignupInviteStatus().catch(() => ({ enabled: false, invite_code: null, invite_url: null })),
        ]);
        setUsername(profile.username || '');
        setInitialUsername(profile.username || '');
        setEmail(profile.email || '');
        setInitialEmail(profile.email || '');
        setEmailVerified(profile.email_verified === true);
        setSignupInvite(signupInviteData || { enabled: false, invite_code: null, invite_url: null });
        setProfileImageUrl(profile.profile_image_url || '');
        setInterestsText((interestsData.interests || []).map((interest) => interest.label).join(', '));
        setNotificationSettings({
          preferences: notificationPreferenceState(notificationsData),
          activeRegistrations: notificationsData.active_push_registrations || 0,
        });
        setNotificationCapability({
          configured: firebasePushConfigured(),
          permission: pushPermissionState(),
          supported: pushSupported,
        });
      } catch (err) {
        setPasswordStatus({ type: 'error', text: err.message || 'Unable to load your profile.' });
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, []);

  const handleUsernameUpdate = async (event) => {
    event.preventDefault();
    const trimmedUsername = username.trim();
    setUsernameStatus({ type: '', text: '' });

    if (!trimmedUsername) {
      setUsernameStatus({ type: 'error', text: 'Username is required.' });
      return;
    }

    setSavingUsername(true);
    try {
      const response = await updateUsername(trimmedUsername);
      setUsername(response.username || trimmedUsername);
      setInitialUsername(response.username || trimmedUsername);
      setUsernameStatus({ type: 'success', text: response.message || 'Username updated.' });
      window.dispatchEvent(new CustomEvent('profileUpdate'));
    } catch (err) {
      setUsernameStatus({ type: 'error', text: err.message || 'Unable to update your username.' });
    } finally {
      setSavingUsername(false);
    }
  };

  const handleInterestsUpdate = async (event) => {
    event.preventDefault();
    setInterestsStatus({ type: '', text: '' });
    const interestsArray = interestsText
      .split(',')
      .map((interest) => interest.trim())
      .filter((interest) => interest.length > 0);

    setSavingInterests(true);
    try {
      const response = await updateUserInterests(interestsArray);
      setInterestsStatus({ type: 'success', text: response.message || 'Interests updated.' });
    } catch (err) {
      setInterestsStatus({ type: 'error', text: err.message || 'Unable to update your interests.' });
    } finally {
      setSavingInterests(false);
    }
  };

  const handleEmailUpdate = async (event) => {
    event.preventDefault();
    setEmailStatus({ type: '', text: '' });
    setSavingEmail(true);
    try {
      const response = await updateEmail(email.trim());
      setEmail(response.email || email.trim());
      setInitialEmail(response.email || email.trim());
      setEmailVerified(response.email_verified === true);
      setEmailStatus({
        type: response.verification_sent === false ? 'warning' : 'success',
        text: response.verification_sent === false
          ? 'Email saved, but verification could not be sent. Try resending it.'
          : response.message || 'Email updated.',
      });
    } catch (error) {
      setEmailStatus({ type: 'error', text: error.data?.message || error.message || 'Unable to update email.' });
    } finally {
      setSavingEmail(false);
    }
  };

  const handleResendVerification = async () => {
    setEmailStatus({ type: '', text: '' });
    setSavingEmail(true);
    try {
      const response = await resendEmailVerification();
      setEmailStatus({
        type: response.verification_sent ? 'success' : 'warning',
        text: response.message,
      });
    } catch (error) {
      setEmailStatus({ type: 'error', text: error.data?.message || error.message || 'Unable to resend verification.' });
    } finally {
      setSavingEmail(false);
    }
  };

  const handleCopyInvite = async () => {
    try {
      await navigator.clipboard.writeText(signupInvite.invite_url);
      setInviteStatus({ type: 'success', text: 'Invite link copied.' });
    } catch (error) {
      setInviteStatus({ type: 'error', text: 'Invite link could not be copied.' });
    }
  };

  const handlePasswordUpdate = async (event) => {
    event.preventDefault();
    setPasswordStatus({ type: '', text: '' });

    if (newPassword !== confirmPassword) {
      setPasswordStatus({ type: 'error', text: 'New passwords do not match.' });
      return;
    }

    setSavingPassword(true);
    try {
      const response = await updatePassword(currentPassword, newPassword);
      setPasswordStatus({ type: 'success', text: response.message || 'Password updated successfully.' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordStatus({ type: 'error', text: err.message || 'Unable to update your password.' });
    } finally {
      setSavingPassword(false);
    }
  };

  const handleNotificationPreference = async (notificationType, enabled) => {
    setSavingNotifications(true);
    setNotificationStatus({ type: '', text: '' });
    try {
      const response = enabled
        ? await enablePushPreference(notificationType)
        : await disablePushPreference(notificationType);
      setNotificationSettings({
        preferences: notificationPreferenceState(response),
        activeRegistrations: response.active_push_registrations || 0,
      });
      setNotificationCapability((current) => ({
        ...current,
        permission: pushPermissionState(),
      }));
      setNotificationStatus({
        type: 'success',
        text: enabled ? 'Notification preference enabled.' : 'Notification preference disabled.',
      });
    } catch (error) {
      setNotificationCapability((current) => ({
        ...current,
        permission: pushPermissionState(),
      }));
      setNotificationStatus({
        type: 'error',
        text: error.message || 'Unable to update notification preferences.',
      });
    } finally {
      setSavingNotifications(false);
    }
  };

  const handleRegisterCurrentDevice = async () => {
    const enabledType = NOTIFICATION_OPTIONS.find(
      ({ type }) => notificationSettings.preferences[type],
    )?.type;
    if (!enabledType) return;
    await handleNotificationPreference(enabledType, true);
  };

  const panelSx = {
    p: { xs: 2, md: 3 },
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'rgba(57, 255, 20, 0.42)',
    backgroundColor: 'rgba(10, 15, 13, 0.92)',
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 }, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
      <Box sx={{ display: 'grid', justifyItems: 'center', gap: 1.25, mb: 3, textAlign: 'center' }}>
        <Avatar
          src={profileImageUrl || '/dope_logo_192.png'}
          alt={`${username || 'User'} profile`}
          sx={{
            width: { xs: 88, sm: 112 },
            height: { xs: 88, sm: 112 },
            border: '3px solid',
            borderColor: 'primary.main',
            bgcolor: '#0A0F0D',
            boxShadow: '0 0 24px rgba(57, 255, 20, 0.24)',
          }}
        />
        <Typography
          variant="h5"
          component="p"
          sx={{
            color: 'primary.main',
            fontWeight: 800,
            maxWidth: '100%',
            overflowWrap: 'anywhere',
          }}
        >
          {username}
        </Typography>
        <Typography variant="h3" component="h1">
          Profile
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Keep your account current and tune the reward ideas that make finishing tasks feel good.
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gap: 2.5 }}>
        <SubscriptionPanel panelSx={panelSx} username={username} />

        <Paper id="profile-notifications-panel" elevation={0} sx={panelSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <BellRing size={22} />
            <Typography variant="h5" component="h2">
              Push notifications
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose the moments when Dopatize should bring you back to the next useful action.
          </Typography>

          {!notificationCapability.configured && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Firebase push is not configured on this deployment yet.
            </Alert>
          )}
          {notificationCapability.configured
            && !notificationCapability.supported
            && notificationCapability.permission !== 'denied' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This browser does not support Firebase push notifications.
            </Alert>
          )}
          {notificationCapability.permission === 'denied' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Notifications are blocked in this browser. Allow them in the site settings,
              then try again.
            </Alert>
          )}
          {Object.values(notificationSettings.preferences).some(Boolean) && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Push is registered for {notificationSettings.activeRegistrations}{' '}
              {notificationSettings.activeRegistrations === 1 ? 'device' : 'devices'}.
            </Alert>
          )}
          {notificationStatus.text && (
            <Alert severity={notificationStatus.type} sx={{ mb: 2 }}>
              {notificationStatus.text}
            </Alert>
          )}

          <Box sx={{ display: 'grid', gap: 1.25 }}>
            {NOTIFICATION_OPTIONS.map(({ type, label, description }) => {
              const enabled = notificationSettings.preferences[type];
              return (
                <Box
                  key={type}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2,
                    p: 1.25,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1">{label}</Typography>
                    <Typography variant="body2" color="text.secondary">{description}</Typography>
                  </Box>
                  <Switch
                    checked={enabled}
                    onChange={(event) => handleNotificationPreference(type, event.target.checked)}
                    disabled={
                      savingNotifications
                      || (!enabled && (
                        !notificationCapability.supported
                        || notificationCapability.permission === 'denied'
                      ))
                    }
                    slotProps={{ input: { 'aria-label': label } }}
                  />
                </Box>
              );
            })}
            {Object.values(notificationSettings.preferences).some(Boolean)
              && !currentPushDeviceRegistered() && (
              <Button
                variant="contained"
                onClick={handleRegisterCurrentDevice}
                disabled={
                  savingNotifications
                  || !notificationCapability.supported
                  || notificationCapability.permission === 'denied'
                }
              >
                {savingNotifications ? 'Enabling...' : 'Enable on this device'}
              </Button>
            )}
          </Box>
        </Paper>

        <Paper elevation={0} sx={panelSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <UserRound size={22} />
            <Typography variant="h5" component="h2">
              Username
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleUsernameUpdate} sx={{ display: 'grid', gap: 2 }}>
            <TextField
              label="Username"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              required
              fullWidth
              inputProps={{ maxLength: 80 }}
            />

            {usernameStatus.text && (
              <Alert severity={usernameStatus.type}>{usernameStatus.text}</Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              disabled={savingUsername || username.trim() === initialUsername}
              startIcon={<Save size={18} />}
            >
              {savingUsername ? 'Saving...' : 'Save Username'}
            </Button>
          </Box>
        </Paper>

        <Paper id="profile-email-panel" elevation={0} sx={panelSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Mail size={22} />
            <Typography variant="h5" component="h2">Email</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Verify your email so invitation sign-ups can be confirmed.
          </Typography>
          {initialEmail && (
            <Alert severity={emailVerified ? 'success' : 'warning'} sx={{ mb: 2 }}>
              {emailVerified ? 'Email verified.' : 'Email verification is still needed.'}
            </Alert>
          )}
          <Box component="form" onSubmit={handleEmailUpdate} sx={{ display: 'grid', gap: 2 }}>
            <TextField
              id="profile-email"
              type="email"
              label="Email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              fullWidth
              inputProps={{ maxLength: 120 }}
            />
            {emailStatus.text && <Alert severity={emailStatus.type}>{emailStatus.text}</Alert>}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={savingEmail || !email.trim() || email.trim() === initialEmail}
                startIcon={<Save size={18} />}
              >
                {savingEmail ? 'Saving...' : 'Save Email'}
              </Button>
              {initialEmail && !emailVerified && (
                <Button
                  type="button"
                  variant="outlined"
                  disabled={savingEmail}
                  onClick={handleResendVerification}
                >
                  Resend Verification
                </Button>
              )}
            </Box>
          </Box>
        </Paper>

        {signupInvite.enabled && (
          <Paper id="profile-signup-invite-panel" elevation={0} sx={panelSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Copy size={22} />
              <Typography variant="h5" component="h2">Share Invite</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Share this link with someone you want to invite to Dopatize.
            </Typography>
            <TextField
              label="Invite code"
              value={signupInvite.invite_code || ''}
              fullWidth
              InputProps={{ readOnly: true }}
              sx={{ mb: 2 }}
            />
            {inviteStatus.text && <Alert severity={inviteStatus.type} sx={{ mb: 2 }}>{inviteStatus.text}</Alert>}
            <Button variant="contained" onClick={handleCopyInvite} startIcon={<Copy size={18} />}>
              Copy Invite Link
            </Button>
          </Paper>
        )}

        <Paper id="profile-interests-panel" elevation={0} sx={panelSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Sparkles size={22} />
            <Typography variant="h5" component="h2">
              Interests
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add a few things you enjoy so rewards can feel more personal.
          </Typography>

          <Box component="form" onSubmit={handleInterestsUpdate} sx={{ display: 'grid', gap: 2 }}>
            <TextField
              label="Interests"
              value={interestsText}
              onChange={(event) => setInterestsText(event.target.value)}
              multiline
              rows={4}
              fullWidth
              helperText="Separate interests with commas."
            />

            {interestsStatus.text && (
              <Alert severity={interestsStatus.type}>{interestsStatus.text}</Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              disabled={savingInterests}
              startIcon={<Save size={18} />}
            >
              {savingInterests ? 'Saving...' : 'Save Interests'}
            </Button>
          </Box>
        </Paper>

        <Paper elevation={0} sx={panelSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <KeyRound size={22} />
            <Typography variant="h5" component="h2">
              Change Password
            </Typography>
          </Box>

          <Box component="form" onSubmit={handlePasswordUpdate} sx={{ display: 'grid', gap: 2.5 }}>
            <TextField
              type="password"
              label="Current password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              required
              fullWidth
            />
            <TextField
              type="password"
              label="New password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              required
              fullWidth
              helperText="Use at least 8 characters."
              inputProps={{ minLength: 8 }}
            />
            <TextField
              type="password"
              label="Confirm new password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              required
              fullWidth
              inputProps={{ minLength: 8 }}
            />

            {passwordStatus.text && (
              <Alert severity={passwordStatus.type}>{passwordStatus.text}</Alert>
            )}

            <Button type="submit" variant="contained" disabled={savingPassword} startIcon={<Save size={18} />}>
              {savingPassword ? 'Updating...' : 'Update Password'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Profile;
